import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Heart, Shield, Star, Clock, Users } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

export const CoachingPage = () => {
  return (
    <div data-testid="coaching-page" className="min-h-screen bg-cream">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-sage/5 to-cream py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 rounded-full mb-6">
            <Heart className="w-4 h-4 text-gold" />
            <span className="text-sm font-medium text-gold">Faith-Led Coaching</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl text-charcoal mb-5">
            12-Week Transformation Coaching Programme
          </h1>
          <p className="text-base md:text-lg text-charcoal-light max-w-2xl mx-auto mb-8">
            Personalised, Christ-centred coaching for women healing from sexual abuse, trauma, and emotional wounds. 
            12 weekly sessions to break through limiting beliefs, release shame and guilt, and reclaim your God-given purpose.
          </p>
          <Link to="/contact" data-testid="coaching-cta-top"
            className="inline-flex items-center gap-2 px-8 py-4 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all shadow-lg">
            Book Your Free Clarity Session <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl text-charcoal text-center mb-10">This Coaching Is For You If...</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              "You've experienced sexual abuse or trauma and feel stuck in the pain",
              "You carry shame, guilt, or self-blame for what happened to you",
              "You struggle with trust, boundaries, or healthy relationships",
              "You've tried to 'move on' but the past keeps pulling you back",
              "You want to forgive but don't know how — or if you even should",
              "You feel disconnected from your body, your identity, or your faith",
              "You know God has a purpose for you but trauma is blocking your path",
              "You're tired of surviving and ready to start thriving"
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm">
                <Check className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
                <span className="text-sm text-charcoal">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Work On */}
      <section className="bg-sage/5 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl text-charcoal text-center mb-10">What We'll Work On Together</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: "Breaking Through Trauma", desc: "Process and release the impact of sexual abuse in a safe, faith-led space" },
              { icon: Heart, title: "Releasing Shame & Guilt", desc: "Separate what happened to you from who you are in God's eyes" },
              { icon: Star, title: "Overcoming Limiting Beliefs", desc: "Replace the lies trauma planted with God's truth about your identity" },
              { icon: Users, title: "Healthy Boundaries", desc: "Learn to set and hold boundaries without guilt in every relationship" },
              { icon: Clock, title: "Forgiveness Journey", desc: "Navigate forgiveness at your own pace — no pressure, no rushing" },
              { icon: Heart, title: "Stepping Into Purpose", desc: "Discover how God can use your story for His glory and your calling" }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-sage/10 flex items-center justify-center">
                  <item.icon className="w-6 h-6 text-sage" />
                </div>
                <h3 className="font-heading text-lg text-charcoal mb-2">{item.title}</h3>
                <p className="text-sm text-charcoal-light">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl text-charcoal text-center mb-10">How It Works</h2>
          <div className="space-y-6 max-w-2xl mx-auto">
            {[
              { step: "1", title: "Book Your Free Breakthrough Clarity Session (1 Hour)", desc: "This is your opportunity to share your story in a safe space, gain clarity on what's holding you back, and see if the programme is right for you. No commitment required." },
              { step: "2", title: "Begin Your 12-Week Programme", desc: "12 weekly 45-minute 1:1 coaching sessions with personalised Christ-centred guidance. We'll work through trauma, shame, and limiting beliefs together." },
              { step: "3", title: "Ongoing Support Between Sessions", desc: "You're never alone on this journey. Receive follow-up support between sessions to help you stay on track and process what comes up." }
            ].map((item, i) => (
              <div key={i} className="flex gap-5">
                <div className="w-12 h-12 rounded-full bg-sage text-white flex items-center justify-center font-heading text-xl flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-heading text-lg text-charcoal mb-1">{item.title}</h3>
                  <p className="text-sm text-charcoal-light">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programme Details */}
      <section className="bg-sage/5 py-16 px-6" id="pricing">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl text-charcoal text-center mb-4">What's Included</h2>
          <p className="text-charcoal-light text-center mb-10">Everything you need for your 12-week transformation</p>
          
          <div className="max-w-2xl mx-auto">
            <div data-testid="coaching-programme-card" className="bg-white rounded-2xl p-8 shadow-md relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gold text-white text-xs font-bold rounded-full uppercase tracking-wider">
                12-Week Programme
              </div>

              <h3 className="font-heading text-2xl text-charcoal text-center mt-2 mb-6">Transformation Coaching Programme</h3>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-charcoal">12 × 45-minute 1:1 coaching sessions</span>
                    <p className="text-sm text-charcoal-light">Weekly sessions tailored to your healing journey</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-charcoal">Personalised Christ-centred healing guidance</span>
                    <p className="text-sm text-charcoal-light">Faith-led approach rooted in scripture and prayer</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-charcoal">Support with releasing trauma, shame & limiting beliefs</span>
                    <p className="text-sm text-charcoal-light">Break free from what's been holding you back</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-charcoal">Follow-up support between sessions</span>
                    <p className="text-sm text-charcoal-light">You're never alone — guidance whenever you need it</p>
                  </div>
                </li>
              </ul>

              {/* Bonus */}
              <div className="bg-gold/10 rounded-xl p-5 mb-8">
                <p className="text-xs font-bold text-gold uppercase tracking-wider mb-2">Bonus Included</p>
                <div className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-charcoal">Breakthrough Clarity Session (1 Hour)</span>
                    <p className="text-sm text-charcoal-light">A FREE initial session to share your story, gain clarity, and see if the programme is right for you</p>
                  </div>
                </div>
              </div>

              {/* Pricing Options */}
              <h4 className="font-heading text-lg text-charcoal text-center mb-4">Flexible Payment Options</h4>
              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-sage/5 rounded-xl p-4 text-center ring-2 ring-gold">
                  <p className="text-xs font-bold text-gold uppercase tracking-wider mb-1">Best Value</p>
                  <span className="text-2xl font-heading text-charcoal">£600</span>
                  <p className="text-xs text-charcoal-light mt-1">one-time payment</p>
                </div>
                <div className="bg-sage/5 rounded-xl p-4 text-center">
                  <p className="text-xs font-bold text-charcoal-light uppercase tracking-wider mb-1">3-Month Plan</p>
                  <span className="text-2xl font-heading text-charcoal">£200</span>
                  <p className="text-xs text-charcoal-light mt-1">per month × 3</p>
                </div>
                <div className="bg-sage/5 rounded-xl p-4 text-center">
                  <p className="text-xs font-bold text-charcoal-light uppercase tracking-wider mb-1">5-Month Plan</p>
                  <span className="text-2xl font-heading text-charcoal">£120</span>
                  <p className="text-xs text-charcoal-light mt-1">per month × 5</p>
                </div>
              </div>

              <Link to="/contact" data-testid="coaching-programme-cta"
                className="block w-full py-3.5 bg-sage text-white rounded-full font-medium text-center hover:bg-sage-dark transition-all shadow-md">
                Book Your Free Clarity Session
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-charcoal mb-4">Your Breakthrough Clarity Session Is Free</h2>
          <p className="text-charcoal-light mb-8">
            A 1-hour session where we'll explore your story, identify what's holding you back, 
            and create a clear path forward. No pressure, no obligation.
          </p>
          <Link to="/contact" data-testid="coaching-cta-bottom"
            className="inline-flex items-center gap-2 px-8 py-4 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all shadow-lg">
            Book Your Free Clarity Session <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};
